import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const BASE = '/api';

// Fetch avec JWT automatique (même logique que api.js)
function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

// ── Icônes Unicode (rendu garanti) ────────────────────────────
const Svg = ({ children, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconSearch  = () => <Svg><path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"/><path d="M21 21l-6 -6"/></Svg>;
const IconUpload  = () => <Svg size={14}><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"/><path d="M7 9l5 -5l5 5"/><path d="M12 4l0 12"/></Svg>;
const IconCheck   = () => <Svg><path d="M5 12l5 5l10 -10"/></Svg>;
const IconX       = () => <Svg size={14}><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></Svg>;
const IconTrash   = () => <Svg size={14}><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></Svg>;
const IconUnlock  = () => <Svg size={14}><path d="M5 11m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z"/><path d="M12 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M8 11v-5a4 4 0 0 1 8 0"/></Svg>;
const IconLock    = () => <Svg size={13}><path d="M5 13m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z"/><path d="M12 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M8 11v-4a4 4 0 0 1 8 0v4"/></Svg>;

// ── Traduction des noms de documents par type ─────────────────
// (les noms sont stockés en français en base — on les traduit côté client)
function getDocLabel(doc, t) {
  const MAP = {
    // RC Pro
    projet_signe:               t('docs_souscription.doc_projet_signe'),
    certificat_immatriculation: t('docs_souscription.doc_certificat'),
    rib:                        t('docs_souscription.doc_rib'),
    releve_sinistre:            t('docs_souscription.doc_releve_sinistre'),
    // Garantie Financière
    gf_cert_immat:               t('docs_souscription.doc_gf_cert_immat'),
    gf_statuts:                  t('docs_souscription.doc_gf_statuts'),
    gf_cv_dirigeant:             t('docs_souscription.doc_gf_cv_dirigeant'),
    gf_piece_id:                 t('docs_souscription.doc_gf_piece_id'),
    gf_presentation_projet:      t('docs_souscription.doc_gf_presentation_projet'),
    gf_previsionnel:             t('docs_souscription.doc_gf_previsionnel'),
    gf_cgv:                      t('docs_souscription.doc_gf_cgv'),
    gf_presentation_activites:   t('docs_souscription.doc_gf_presentation_activites'),
    gf_previsionnel_annuel:      t('docs_souscription.doc_gf_previsionnel_annuel'),
    gf_bilan_cr:                 t('docs_souscription.doc_gf_bilan_cr'),
    gf_attestation_rcpro:        t('docs_souscription.doc_gf_attestation_rcpro'),
    gf_attestation_fonds:        t('docs_souscription.doc_gf_attestation_fonds'),
  };
  return MAP[doc.type] || doc.nom;
}

// ── Statut styles ─────────────────────────────────────────────
const SS = {
  attente: { bg:'#F1F5F9', color:'#64748B', dot:'#CBD5E1' },
  uploade: { bg:'#FEF3C7', color:'#92400E', dot:'#F59E0B' },
  accepte: { bg:'#D1FAE5', color:'#065F46', dot:'#10B981' },
  refuse:  { bg:'#FEE2E2', color:'#991B1B', dot:'#EF4444' },
};

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024*1024) return `${Math.round(b/1024)} Ko`;
  return `${(b/1024/1024).toFixed(1)} Mo`;
}

// ── Bouton icône avec tooltip ─────────────────────────────────
function IconBtn({ icon, label, onClick, bg='#F8FAFC', color='#475569', border='#E2E8F0', disabled=false }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{position:'relative', display:'inline-block', flexShrink:0}}>
      <button onClick={onClick} disabled={disabled}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width:34, height:34, borderRadius:8, border:`1.5px solid ${border}`,
          background:bg, color, cursor:disabled?'not-allowed':'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          opacity:disabled?.4:1, transition:'filter .1s', padding:0,
        }}>
        {icon}
      </button>
      {hovered && !disabled && (
        <div style={{
          position:'fixed',
          background:'#1A1A1A', color:'#fff', fontSize:11.5, fontWeight:600,
          padding:'5px 10px', borderRadius:6, whiteSpace:'nowrap',
          zIndex:99999, pointerEvents:'none',
          transform:'translateX(-50%)',
          marginTop:4,
        }}
          ref={el => {
            if (el) {
              const btn = el.previousSibling?.getBoundingClientRect
                ? el.previousSibling.getBoundingClientRect()
                : null;
              if (btn) {
                el.style.top  = (btn.bottom + 6) + 'px';
                el.style.left = (btn.left + btn.width/2) + 'px';
              }
            }
          }}>
          {label}
        </div>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────
export default function DocsSouscription({ policeId, produit, contexte, dejaAssure, fractionnement, readOnly: readOnlyProp = false, locked = false, docsLocked = false, onDocsLockChange, onStatusChange, onPoliceStatut }) {
  const { t } = useLanguage();
  const { isCourtier, isClient, isArcus, isReadOnly } = useAuth();
  // Courtier + Client : upload + view seulement — pas d'acceptation/refus/suppression.
  // "locked" (dossier soumis / en cours d'étude) : plus aucun dépôt possible non plus.
  // "docsLocked" (pack verrouillé par l'admin) : tout est gelé.
  const readOnly = readOnlyProp || isCourtier || isClient || docsLocked;
  const [docs, setDocs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [addingDoc, setAddingDoc]     = useState(false);
  const [newDocNom, setNewDocNom]     = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [previewDoc, setPreviewDoc]   = useState(null);
  const [previewUrl, setPreviewUrl]   = useState(null);
  const [refuseDoc, setRefuseDoc]         = useState(null);
  const [refuseComment, setRefuseComment] = useState('');
  const [refuseCategorie, setRefuseCategorie]   = useState('');
  const [refuseError, setRefuseError]         = useState('');
  const [refuseSaving, setRefuseSaving]   = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCopied, setEmailCopied]     = useState(false);
  const [packBusy, setPackBusy]           = useState(false);
  const [error, setError]                 = useState('');
  const fileInputRef   = useRef(null);
  const activeUploadId = useRef(null);

  useEffect(() => { if (policeId) initDocs(); }, [policeId]);

  async function initDocs() {
    setLoading(true);
    try {
      const res  = await authFetch(`${BASE}/polices/${policeId}/docs-souscription/init`, {
        method:'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          produit:          produit     || undefined,
          contexte_gf:      contexte    || undefined,
          deja_assure:      dejaAssure  !== undefined ? dejaAssure  : undefined,
          fractionnement:   fractionnement || undefined,
        }),
      });
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      try {
        const res  = await authFetch(`${BASE}/polices/${policeId}/docs-souscription`);
        const data = await res.json();
        setDocs(Array.isArray(data) ? data : []);
      } catch(e) { setError(e.message); }
    } finally { setLoading(false); }
  }

  async function ouvrirPreview(doc) {
    try {
      const token = localStorage.getItem('arcus_token');
      const res = await fetch(`${BASE}/docs-souscription/${doc.id}/fichier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { setError('Impossible de charger le fichier'); return; }
      const raw  = await res.blob();
      // Force le type MIME connu (en base) : garantit l'affichage PDF/image
      // même si le serveur renvoie un Content-Type générique (octet-stream).
      const blob = doc.fichier_mime ? new Blob([raw], { type: doc.fichier_mime }) : raw;
      const url  = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewDoc(doc);
    } catch(e) { setError(e.message); }
  }

  function fermerPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDoc(null);
  }

  function triggerUpload(docId) {
    activeUploadId.current = docId;
    fileInputRef.current.click();
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file || !activeUploadId.current) return;
    e.target.value = '';
    const docId = activeUploadId.current;
    activeUploadId.current = null;
    setUploadingId(docId);
    try {
      const form = new FormData();
      form.append('fichier', file);
      const res  = await authFetch(`${BASE}/docs-souscription/${docId}/upload`, { method:'POST', body:form });
      const data = await res.json();
      if (data.error) { setRefuseError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
    } catch(e) { setError(e.message); }
    finally { setUploadingId(null); }
  }

  async function accepter(docId) {
    if (!window.confirm(t('docs_souscription.accept_confirm'))) return;
    try {
      const res  = await authFetch(`${BASE}/docs-souscription/${docId}/accepter`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setRefuseError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
    } catch(e) { setError(e.message); }
  }

  async function deverrouiller(docId) {
    if (!window.confirm(t('docs_souscription.unlock_confirm'))) return;
    try {
      const res  = await authFetch(`${BASE}/docs-souscription/${docId}/deverrouiller`, { method:'POST' });
      const data = await res.json();
      if (data.error) { setRefuseError(data.error); return; }
      setDocs(prev => prev.map(d => d.id===docId ? data : d));
    } catch(e) { setError(e.message); }
  }

  async function confirmerRefus() {
    if (!refuseCategorie) return; // catégorie obligatoire
    setRefuseSaving(true);
    try {
      const res  = await authFetch(`${BASE}/docs-souscription/${refuseDoc.id}/refuser`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ categorie_refus: refuseCategorie, commentaire: refuseComment }),
      });
      const data = await res.json();
      if (data.error) { setRefuseError(data.error); return; }
      const { _police_statut, ...docData } = data;
      setDocs(prev => prev.map(d => d.id === refuseDoc.id ? docData : d));
      // Le refus d'une pièce GF peut faire repasser la police en "Demande de pièces".
      // On prévient le parent pour qu'il rafraîchisse l'état (déverrouillage du dépôt).
      if (_police_statut && onPoliceStatut) onPoliceStatut(_police_statut);
      setRefuseDoc(null); setRefuseComment(''); setRefuseCategorie(''); setRefuseError('');
    } catch(e) { setRefuseError(e.message); }
    finally { setRefuseSaving(false); }
  }

  async function supprimer(docId) {
    if (!window.confirm(t('docs_souscription.delete_confirm'))) return;
    try {
      await authFetch(`${BASE}/docs-souscription/${docId}`, { method:'DELETE' });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch(e) { setError(e.message); }
  }

  async function addCustomDoc() {
    if (!newDocNom.trim()) return;
    try {
      const res  = await authFetch(`${BASE}/polices/${policeId}/docs-souscription`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ nom: newDocNom.trim() }),
      });
      const data = await res.json();
      setDocs(prev => [...prev, data]);
      setNewDocNom(''); setAddingDoc(false);
    } catch(e) { setError(e.message); }
  }

  function buildEmailBody() {
    const refused = docs.filter(d => d.statut === 'refuse');
    const lines = refused.map(d => {
      const label   = getDocLabel(d, t);
      const cat     = d.categorie_refus || '—';
      const comment = d.commentaire_refus ? ` — ${d.commentaire_refus}` : '';
      return `• ${label} > ${cat}${comment}`;
    }).join('\n');
    return `Madame, Monsieur,\n\nDans le cadre de votre demande de garantie financière, nous avons analysé les pièces que vous nous avez faites parvenir. À ce jour, le/les document(s) suivant(s) a/ont été refusé(s) :\n\n${lines}\n\nNous vous remercions de bien vouloir nous faire parvenir les pièces corrigées dans les meilleurs délais.\n\nCordialement,\nL'équipe Arcus Solutions`;
  }

  function copierEmail() {
    navigator.clipboard.writeText(buildEmailBody()).then(() => {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    });
  }

  const accepted = docs.filter(d => d.statut==='accepte').length;
  const total    = docs.length;
  const pct      = total > 0 ? Math.round((accepted/total)*100) : 0;

  // Remonter au parent l'état de complétude des documents (pour le bouton de soumission GF).
  // "allProvided" = tous les documents requis ont un fichier déposé (uploadé ou accepté),
  // c.-à-d. aucun document requis en statut 'attente' ou 'refuse'.
  useEffect(() => {
    if (!onStatusChange) return;
    const required = docs.filter(d => d.requis);
    const missing  = required.filter(d => d.statut === 'attente' || d.statut === 'refuse');
    onStatusChange({
      total,
      accepted,
      requiredTotal: required.length,
      missingCount: missing.length,
      allProvided: required.length > 0 && missing.length === 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  // Tout validé = toutes les pièces requises sont acceptées (pré-requis verrou / ZIP).
  const requiredDocs = docs.filter(d => d.requis);
  const allAccepted = requiredDocs.length > 0 && requiredDocs.every(d => d.statut === 'accepte');
  const canManagePack = isArcus && !isReadOnly;

  async function toggleDocsLock(next) {
    const msg = next ? t('docs_souscription.lock_confirm') : t('docs_souscription.unlock_pack_confirm');
    if (!window.confirm(msg)) return;
    setPackBusy(true);
    try {
      const res = await authFetch(`${BASE}/polices/${policeId}/verrouiller-docs-gf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ locked: next }),
      });
      const data = await res.json();
      if (!res.ok) { window.alert(data.error || 'Erreur'); return; }
      if (onDocsLockChange) onDocsLockChange(!!data.docs_souscription_verrouilles);
    } catch (e) { window.alert(e.message); }
    finally { setPackBusy(false); }
  }

  function generateZip() {
    if (!window.confirm(t('docs_souscription.zip_confirm'))) return;
    // Placeholder — la génération réelle (avec renommage des PDF) viendra plus tard.
    window.alert(t('docs_souscription.zip_coming'));
  }

  if (loading) return <div style={{color:'#64748B',padding:8}}>{t('common.loading')}</div>;

  return (
    <div>
      <input type="file" ref={fileInputRef} style={{display:'none'}}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
        onChange={handleFileChange}/>

      {/* ── Modal Refus ─────────────────────────────────────── */}
      {refuseDoc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:460,maxWidth:'90vw'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4,color:'#DC2626'}}>{t('docs_souscription.refuse_title')}</div>
            <div style={{fontSize:13,color:'#64748B',marginBottom:16}}>
              <strong>{getDocLabel(refuseDoc, t)}</strong>
            </div>
            {/* Catégorie — obligatoire */}
            <label style={{fontSize:12,color:'#374151',display:'block',marginBottom:5,fontWeight:700}}>
              {t('docs_souscription.refuse_category')}
            </label>
            <select value={refuseCategorie} onChange={e=>setRefuseCategorie(e.target.value)} autoFocus
              style={{border:`1.5px solid ${refuseCategorie?'#E2E8F0':'#FCA5A5'}`,borderRadius:7,
                padding:'8px 10px',width:'100%',fontSize:13,boxSizing:'border-box',
                fontFamily:'inherit',marginBottom:14,background:'#fff'}}>
              <option value="">{t('docs_souscription.refuse_category_placeholder')}</option>
              {['Document illisible','Document non conforme','Document incomplet'].map(c=>(
                <option key={c}>{c}</option>
              ))}
            </select>
            {/* Précision — optionnelle */}
            <label style={{fontSize:12,color:'#374151',display:'block',marginBottom:5,fontWeight:700}}>
              {t('docs_souscription.refuse_detail')}
            </label>
            <textarea value={refuseComment} onChange={e=>setRefuseComment(e.target.value)}
              placeholder={t('docs_souscription.refuse_placeholder')} rows={3}
              style={{border:'1px solid #E2E8F0',borderRadius:6,padding:'8px 10px',width:'100%',
                fontSize:13,boxSizing:'border-box',resize:'vertical',fontFamily:'inherit',marginBottom:16}}/>
            {refuseError && (
              <div style={{background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:6,
                padding:'8px 12px',fontSize:12,color:'#991B1B',marginBottom:12}}>
                ⚠ {refuseError}
              </div>
            )}
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>{setRefuseDoc(null);setRefuseComment('');setRefuseCategorie('');setRefuseError('');}}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {t('common.cancel')}
              </button>
              <button onClick={confirmerRefus} disabled={!refuseCategorie||refuseSaving}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#DC2626',color:'#fff',
                  border:'none',cursor:'pointer',fontWeight:700,opacity:(!refuseCategorie||refuseSaving)?.5:1}}>
                {refuseSaving?t('common.saving'):t('docs_souscription.refuse_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Preview ───────────────────────────────────── */}
      {previewDoc && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}
          onClick={fermerPreview}>
          <div style={{background:'#fff',borderRadius:12,overflow:'hidden',
            width:'88vw',maxWidth:960,maxHeight:'92vh',display:'flex',flexDirection:'column'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:'11px 16px',borderBottom:'1px solid #F1F5F9',display:'flex',alignItems:'center',gap:10,background:'#F8FAFC'}}>
              <div style={{flex:1,fontWeight:700,fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {previewDoc.fichier_nom}
              </div>
              <span style={{fontSize:12,color:'#64748B'}}>{fmtSize(previewDoc.fichier_taille)}</span>
              <a href={previewUrl} target="_blank" rel="noreferrer"
                style={{fontSize:13,padding:'5px 12px',borderRadius:6,background:'#1A1A1A',color:'#fff',textDecoration:'none',fontWeight:600}}>
                ⬇ {t('common.download')}
              </a>
              <button onClick={()=>setPreviewDoc(null)}
                style={{fontSize:20,background:'none',border:'none',cursor:'pointer',color:'#64748B',padding:'0 4px'}}>✕</button>
            </div>
            <div style={{flex:1,overflow:'auto',background:'#F8FAFC',minHeight:400}}>
              {previewDoc.fichier_mime?.startsWith('image/') ? (
                <img src={previewUrl} alt={previewDoc.fichier_nom}
                  style={{maxWidth:'100%',display:'block',margin:'16px auto',borderRadius:4,boxShadow:'0 2px 12px rgba(0,0,0,.15)'}}/>
              ) : previewDoc.fichier_mime==='application/pdf' ? (
                <iframe src={previewUrl}
                  title={previewDoc.fichier_nom} style={{width:'100%',height:'72vh',border:'none'}}/>
              ) : (
                <div style={{padding:40,textAlign:'center',color:'#64748B'}}>
                  <div style={{fontSize:56,marginBottom:14}}>📄</div>
                  <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>{previewDoc.fichier_nom}</div>
                  <a href={previewUrl} download
                    style={{color:'#2563EB',fontSize:14}}>{t('common.download')}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {readOnly && (
        <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:7,
          padding:'8px 12px',marginBottom:12,fontSize:13,color:'#1D4ED8',display:'flex',gap:8,alignItems:'center'}}>
          <span style={{color:'#1D4ED8',display:'inline-flex'}}><IconLock/></span> {t('docs_souscription.readonly_banner')}
        </div>
      )}
      {error && (
        <div style={{background:'#FEE2E2',color:'#991B1B',borderRadius:6,padding:'8px 12px',marginBottom:12,fontSize:13,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          {error}
          <button onClick={()=>setError('')} style={{background:'none',border:'none',cursor:'pointer',color:'#991B1B',fontWeight:700,fontSize:15}}>✕</button>
        </div>
      )}

      {/* ── Header + barre de progression ───────────────────── */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
        <div>
          <div style={{fontSize:15,fontWeight:700}}>{t('docs_souscription.title')}</div>
          <div style={{fontSize:12,color:'#64748B',marginTop:2}}>{t('docs_souscription.subtitle')}</div>
        </div>
        <div style={{textAlign:'right',minWidth:130}}>
          <div style={{fontSize:13,fontWeight:700,color:pct===100?'#059669':'#475569'}}>
            {accepted}/{total} {t('docs_souscription.completion')}
          </div>
          <div style={{marginTop:6,height:5,background:'#F1F5F9',borderRadius:99,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${pct}%`,background:pct===100?'#10B981':'#E8671A',borderRadius:99,transition:'width .3s'}}/>
          </div>
        </div>
      </div>

      {/* ── Liste des documents ─────────────────────────────── */}
      <div style={{border:'1px solid #E2E8F0',borderRadius:10,background:'#fff'}}>
        {docs.map((doc, idx) => {
          const ss = SS[doc.statut] || SS.attente;
          const isUploading = uploadingId===doc.id;
          const isAccepted  = doc.statut==='accepte';
          const isUploaded  = doc.statut==='uploade';
          const isRefused   = doc.statut==='refuse';

          return (
            <div key={doc.id}
              style={{borderBottom: idx<docs.length-1?'1px solid #F1F5F9':'none',
                borderRadius: idx===0?'10px 10px 0 0': idx===docs.length-1?'0 0 10px 10px':'0'}}>
              {/* Ligne principale */}
              <div style={{
                display:'flex', alignItems:'center', gap:10, padding:'12px 14px',
                background: isAccepted?'#F0FDF4': isRefused?'#FFF5F5':'#fff',
                borderRadius: 'inherit',
              }}>
                {/* Point statut */}
                <div style={{width:9,height:9,borderRadius:'50%',background:ss.dot,flexShrink:0}}/>

                {/* Nom + infos */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
                    <span style={{fontSize:13.5,fontWeight:600,
                      color:isAccepted?'#065F46':isRefused?'#991B1B':'#1A1A1A'}}>
                      {getDocLabel(doc, t)}
                    </span>
                    {doc.requis && !isAccepted && (
                      <span style={{fontSize:10,padding:'1px 6px',borderRadius:99,
                        background:'#FEF3C7',color:'#92400E',fontWeight:700,flexShrink:0}}>
                        requis
                      </span>
                    )}
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,
                      background:ss.bg,color:ss.color,fontWeight:700,flexShrink:0}}>
                      {doc.statut ? t(`docs_souscription.status_${doc.statut}`) : t('docs_souscription.status_attente')}
                    </span>
                  </div>
                  {doc.fichier_nom && (
                    <div style={{fontSize:11,color:'#64748B',marginTop:3,display:'flex',alignItems:'center',gap:5}}>
                      <span>📎</span>
                      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:260}}>
                        {doc.fichier_nom}
                      </span>
                      {doc.fichier_taille && <span style={{color:'#94A3B8',flexShrink:0}}>({fmtSize(doc.fichier_taille)})</span>}
                    </div>
                  )}
                  {doc.type==='rib' && (
                    <div style={{fontSize:11,color:'#2563EB',marginTop:2}}>ℹ {t('docs_souscription.rib_condition')}</div>
                  )}
                  {doc.type==='releve_sinistre' && (
                    <div style={{fontSize:11,color:'#2563EB',marginTop:2}}>ℹ {t('docs_souscription.releve_condition')}</div>
                  )}
                </div>

                {/* ── Boutons actions ── */}
                <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>

                  {/* Visualiser */}
                  {doc.fichier_nom && (
                    <IconBtn icon={<IconSearch/>} label={t('docs_souscription.preview_btn')}
                      onClick={()=>ouvrirPreview(doc)}
                      bg='#EFF6FF' color='#2563EB' border='#BFDBFE'/>
                  )}

                  {/* Upload / Remplacer — bloqué si le dossier est soumis (locked) ou le pack verrouillé */}
                  {!isAccepted && !locked && !docsLocked && (
                    <IconBtn icon={isUploading?<span style={{fontSize:11}}>…</span>:<IconUpload/>}
                      label={doc.fichier_nom?t('docs_souscription.replace_btn'):t('docs_souscription.upload_btn')}
                      onClick={()=>triggerUpload(doc.id)} disabled={isUploading}
                      bg='#F8FAFC' color='#475569' border='#E2E8F0'/>
                  )}

                  {/* Accepter — masqué en lecture seule */}
                  {isUploaded && !readOnly && (
                    <IconBtn icon={<IconCheck/>} label={t('docs_souscription.accept_btn')}
                      onClick={()=>accepter(doc.id)}
                      bg='#D1FAE5' color='#065F46' border='#6EE7B7'/>
                  )}

                  {/* Refuser — masqué en lecture seule */}
                  {isUploaded && !readOnly && (
                    <IconBtn icon={<IconX/>} label={t('docs_souscription.refuse_btn')}
                      onClick={()=>{setRefuseDoc(doc);setRefuseComment('');setRefuseCategorie('');setRefuseError('');}}
                      bg='#FEE2E2' color='#DC2626' border='#FCA5A5'/>
                  )}

                  {/* Déverrouiller — masqué en lecture seule */}
                  {isAccepted && !readOnly && (
                    <>
                      <span style={{fontSize:12,color:'#059669',fontWeight:700,display:'flex',alignItems:'center',gap:4}}>
                        <IconLock/> {t('docs_souscription.locked_msg')}
                      </span>
                      <IconBtn icon={<IconUnlock/>} label={t('docs_souscription.unlock_btn')}
                        onClick={()=>deverrouiller(doc.id)}
                        bg='#FEF3C7' color='#92400E' border='#FDE68A'/>
                    </>
                  )}

                  {/* Supprimer — masqué en lecture seule */}
                  {!isAccepted && !readOnly && (
                    <IconBtn icon={<IconTrash/>} label={t('common.delete')}
                      onClick={()=>supprimer(doc.id)}
                      bg='#FFF5F5' color='#DC2626' border='#FCA5A5'/>
                  )}
                </div>
              </div>

              {/* Motif refus */}
              {isRefused && (
                <div style={{margin:'0 14px 12px 33px',background:'#FFF5F5',border:'1px solid #FCA5A5',
                  borderRadius:6,padding:'8px 12px',fontSize:12.5,color:'#7F1D1D',
                  display:'flex',flexDirection:'column',gap:4}}>
                  {doc.categorie_refus
                    ? <span style={{display:'inline-block',background:'#FEE2E2',color:'#991B1B',
                        borderRadius:99,padding:'2px 10px',fontSize:11,fontWeight:700,width:'fit-content'}}>
                        ✕ {doc.categorie_refus}
                      </span>
                    : <span style={{fontWeight:700,color:'#991B1B'}}>✕ Document refusé</span>
                  }
                  {doc.commentaire_refus && (
                    <div style={{fontSize:12,color:'#7F1D1D'}}>
                      <strong>{t('docs_souscription.rejected_comment')}</strong> {doc.commentaire_refus}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {docs.length===0 && (
          <div style={{padding:24,textAlign:'center',color:'#94A3B8',fontSize:13}}>{t('common.no_results')}</div>
        )}
      </div>

      {/* ── Bouton email refus (souscripteur uniquement) ─────── */}
      {!readOnly && docs.some(d=>d.statut==='refuse') && (
        <div style={{marginTop:12,marginBottom:4}}>
          <button onClick={()=>setShowEmailModal(true)}
            style={{width:'100%',padding:'10px',background:'#FFF7F2',border:'1px solid #FED7AA',
              borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:700,color:'#C2410C',
              display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
            {t('docs_souscription.refuse_email_btn')}
          </button>
        </div>
      )}

      {/* ── Modal Email ──────────────────────────────────────── */}
      {showEmailModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:580,maxWidth:'92vw',
            maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>
              {t('docs_souscription.refuse_email_title')}
            </div>
            <div style={{fontSize:12,color:'#64748B',marginBottom:12}}>
              Objet : <strong>{t('docs_souscription.refuse_email_subject')}</strong>
            </div>
            <pre style={{flex:1,overflowY:'auto',background:'#F8FAFC',border:'1px solid #E2E8F0',
              borderRadius:7,padding:14,fontSize:12.5,whiteSpace:'pre-wrap',wordBreak:'break-word',
              fontFamily:'inherit',lineHeight:1.7,marginBottom:14}}>
              {buildEmailBody()}
            </pre>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowEmailModal(false)}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {t('common.close')}
              </button>
              <button onClick={copierEmail}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',
                  cursor:'pointer',background:emailCopied?'#D1FAE5':'#fff',
                  color:emailCopied?'#065F46':'#1A1A1A',fontWeight:600}}>
                {emailCopied ? t('docs_souscription.refuse_email_copied') : t('docs_souscription.refuse_email_copy')}
              </button>
              <button onClick={()=>window.open(`mailto:?subject=${encodeURIComponent(t('docs_souscription.refuse_email_subject'))}&body=${encodeURIComponent(buildEmailBody())}`,'_blank')}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#E8671A',
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:700}}>
                {t('docs_souscription.refuse_email_open')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ajouter un document — masqué en lecture seule */}
      {!readOnly && <div style={{marginTop:12}}>
        {!addingDoc ? (
          <button onClick={()=>setAddingDoc(true)}
            style={{fontSize:14,color:'#2563EB',background:'none',border:'1px dashed #93C5FD',
              borderRadius:7,padding:'9px 16px',cursor:'pointer',width:'100%',fontWeight:600}}>
            {t('docs_souscription.add_doc')}
          </button>
        ) : (
          <div style={{background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,padding:14}}>
            <label style={{fontSize:12,color:'#64748B',display:'block',marginBottom:5,fontWeight:700}}>
              {t('docs_souscription.add_doc_label')}
            </label>
            <div style={{display:'flex',gap:8}}>
              <input value={newDocNom} onChange={e=>setNewDocNom(e.target.value)}
                placeholder={t('docs_souscription.add_doc_placeholder')}
                onKeyDown={e=>{
                  if(e.key==='Enter') addCustomDoc();
                  if(e.key==='Escape'){ setAddingDoc(false); setNewDocNom(''); }
                }}
                style={{flex:1,border:'1px solid #E2E8F0',borderRadius:6,padding:'7px 10px',fontSize:14,fontFamily:'inherit'}}
                autoFocus/>
              <button onClick={addCustomDoc} disabled={!newDocNom.trim()}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#1A1A1A',color:'#fff',
                  border:'none',cursor:'pointer',fontWeight:700,opacity:!newDocNom.trim()?.5:1}}>
                {t('common.confirm')}
              </button>
              <button onClick={()=>{setAddingDoc(false);setNewDocNom('');}}
                style={{fontSize:14,padding:'7px 14px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                {t('common.cancel')}
              </button>
            </div>
            <div style={{fontSize:11,color:'#94A3B8',marginTop:5}}>{t('docs_souscription.file_size')}</div>
          </div>
        )}
      </div>}

      {/* Barre — verrou du pack + ZIP (Arcus, dès que tout est validé) */}
      {canManagePack && (allAccepted || docsLocked) && (
        <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #E2E8F0',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',justifyContent:'space-between'}}>
          <div style={{fontSize:12.5,fontWeight:600,color:docsLocked?'#065F46':'#64748B',display:'inline-flex',alignItems:'center',gap:6}}>
            {docsLocked ? <>🔒 {t('docs_souscription.pack_locked_note')}</> : <>✓ {t('docs_souscription.pack_ready_note')}</>}
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button onClick={generateZip} disabled={packBusy || !allAccepted}
              style={{fontSize:13,padding:'8px 15px',borderRadius:8,background:'#fff',color:'#1A1A1A',border:'1px solid #CBD5E1',cursor:(packBusy||!allAccepted)?'not-allowed':'pointer',fontWeight:600,opacity:(packBusy||!allAccepted)?.5:1}}>
              📦 {t('docs_souscription.generate_zip')}
            </button>
            {docsLocked
              ? <button onClick={()=>toggleDocsLock(false)} disabled={packBusy}
                  style={{fontSize:13,padding:'8px 15px',borderRadius:8,background:'#fff',color:'#9A5A00',border:'1px solid #FCD9A6',cursor:'pointer',fontWeight:700,opacity:packBusy?.6:1}}>
                  🔓 {t('docs_souscription.unlock_pack')}
                </button>
              : <button onClick={()=>toggleDocsLock(true)} disabled={packBusy}
                  style={{fontSize:13,padding:'8px 16px',borderRadius:8,background:'#059669',color:'#fff',border:'none',cursor:'pointer',fontWeight:700,opacity:packBusy?.6:1}}>
                  🔒 {t('docs_souscription.lock_pack')}
                </button>}
          </div>
        </div>
      )}
    </div>
  );
}
